import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { useDropzone } from 'react-dropzone';
import { apiFetch } from '../../utils/api';
import { AppSettings } from '../../config/app-settings.js';

import ProfileSection from './ProfileSection';
import PasswordSection from './PasswordSection';
import CompanySection from './CompanySection';
import InstagramSection from './InstagramSection';

function SettingsPage() {
  const context = useContext(AppSettings);
  const user_data = context.userInfo;

  // ==========================================
  // 1. スクロールスパイ
  // ==========================================
  useEffect(() => {
    const sections = document.querySelectorAll('#bsSpyContent > div');
    const navLinks = document.querySelectorAll('#bsSpyTarget > a');

    function activateNavLink(id) {
      navLinks.forEach((link) => { if (link && link.classList) link.classList.remove('active'); });
      var target = document.querySelector(`nav a[href*='${id}']`);
      if (target) target.classList.add('active');
    }

    function isElementInViewport(el) {
      const rect = el.getBoundingClientRect();
      return (rect.top >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight));
    }

    function handleViewport() {
      let activeSection = null;
      for (let i = sections.length - 1; i >= 0; i--) {
        if (isElementInViewport(sections[i])) { activeSection = sections[i].getAttribute('id'); activateNavLink(activeSection); break; }
      }
      let combinedHeight = 0;
      let sectionIndex = Array.from(sections).findIndex((section) => section.getAttribute('id') === activeSection);
      if (sectionIndex !== -1) {
        for (let i = sectionIndex; i < sections.length; i++) { combinedHeight += sections[i].offsetHeight; }
        if (combinedHeight <= window.innerHeight) activateNavLink(activeSection);
      }
    }
    window.onscroll = handleViewport;
    handleViewport();
  }, []);

  // ==========================================
  // 2. ステート管理
  // ==========================================
  const [profile, setProfile] = useState({ first_name: '', last_name: '', nickname: '', email: '', profile_image: '' });
  const [company, setCompany] = useState({
    uuid: '', name_of_company: '', email: '', name_of_representative: '',
    zipcode: '', address1: '', address2: '', address3: '', address4: '',
    phone: '', phone2: '', fax: '', memo: ''
  });
  const [authorities, setAuthorities] = useState([]);
  const [instagramName, setInstagramName] = useState('');
  const [registeredUsers, setRegisteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncingUser, setSyncingUser] = useState(null);
  const [notification, setNotification] = useState({ type: '', text: '' });

  const [modalMode, setModalMode] = useState('name');
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const [editForm, setEditForm] = useState({
    first_name: '', last_name: '', nickname: '', email: '',
    current_password: '', new_password: '', confirm_password: '',
    name_of_company: '', company_email: '', name_of_representative: '',
    zipcode: '', address1: '', address2: '', address3: '', address4: '',
    phone: '', phone2: '', fax: '', memo: ''
  });

  useEffect(() => {
    fetchInitialData();
  }, [user_data]);

  const fetchInitialData = async () => {
    try {
      if (user_data) {
        const authUser = user_data.user_info;
        const comps = user_data.companies;

        setProfile({
          first_name: authUser.first_name || '',
          last_name: authUser.last_name || '',
          nickname: authUser.nickname || '',
          email: authUser.email || '',
          profile_image: authUser.profile || ''
        });

        setAuthorities(user_data.authorities || []);

        if (comps && comps.length > 0) {
          const companyUuid = comps[0].uuid;
          const compRes = await apiFetch(`/companies/${companyUuid}`);
          if (compRes.ok) {
            const c = await compRes.json();
            setCompany({
              uuid: c.uuid, name_of_company: c.name_of_company || '', email: c.email || '', name_of_representative: c.name_of_representative || '',
              zipcode: c.zipcode || '', address1: c.address1 || '', address2: c.address2 || '', address3: c.address3 || '', address4: c.address4 || '',
              phone: c.phone || '', phone2: c.phone2 || '', fax: c.fax || '', memo: c.memo || ''
            });
          }
        }
      }
      const instaRes = await apiFetch('/instagrams/accounts');
      if (instaRes.ok) setRegisteredUsers(await instaRes.json());
    } catch (error) {
      console.error("初期データの取得に失敗しました", error);
    }
  };

  const canEditCompany = authorities.some(auth => ['developer', 'super_admin', 'admin'].includes(auth.role));

  const showNotification = (type, text) => {
    setNotification({ type, text });
    setTimeout(() => setNotification({ type: '', text: '' }), 4000);
  };

  const onDropImage = useCallback(acceptedFiles => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setSelectedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onDropImage, accept: { 'image/*': [] }, multiple: false
  });

  const openEditModal = (mode) => {
    setModalMode(mode);
    if (mode === 'image') {
      setSelectedImage(null);
      setPreviewUrl(null);
    }

    setEditForm({
      first_name: profile.first_name, last_name: profile.last_name, nickname: profile.nickname, email: profile.email,
      current_password: '', new_password: '', confirm_password: '',
      name_of_company: company.name_of_company, company_email: company.email, name_of_representative: company.name_of_representative,
      zipcode: company.zipcode, address1: company.address1, address2: company.address2, address3: company.address3, address4: company.address4,
      phone: company.phone, phone2: company.phone2, fax: company.fax, memo: company.memo
    });
  };

  const handleDeleteImage = async () => {
    if (!window.confirm("プロフィール画像を削除して初期アイコンに戻しますか？")) return;
    setLoading(true);
    try {
      const res = await apiFetch('/auth/me/image', { method: 'DELETE' });
      if (res.ok) {
        setProfile({ ...profile, profile_image: '' });
        if (context.setUserInfo) {
          context.setUserInfo(prev => ({
            ...prev,
            user_info: { ...prev.user_info, profile: null }
          }));
        }
        showNotification('success', '画像を削除しました。');
        document.getElementById('btn-close-modal').click();
        document.activeElement.blur();
      } else {
        showNotification('error', '画像の削除に失敗しました。');
      }
    } catch (error) {
      showNotification('error', '通信エラーが発生しました。');
    } finally {
      setLoading(false);
    }
  };

  const handleModalSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (modalMode === 'image') {
        const formData = new FormData();
        formData.append('file', selectedImage);
        const res = await apiFetch('/auth/me/image', { method: 'POST', body: formData });
        if (res.ok) {
          const data = await res.json();
          setProfile({ ...profile, profile_image: data.filename });
          if (context.setUserInfo) {
            context.setUserInfo(prev => ({
              ...prev,
              user_info: { ...prev.user_info, profile: data.filename }
            }));
          }
          showNotification('success', 'プロフィール画像を更新しました。');
          document.getElementById('btn-close-modal').click();
          document.activeElement.blur();
        } else {
          showNotification('error', '画像のアップロードに失敗しました。');
        }
      }
      else if (modalMode === 'name') {
        const res = await apiFetch('/auth/me', { method: 'PUT', body: JSON.stringify({ first_name: editForm.first_name, last_name: editForm.last_name, nickname: editForm.nickname }) });
        if (res.ok) { setProfile({ ...profile, first_name: editForm.first_name, last_name: editForm.last_name, nickname: editForm.nickname }); showNotification('success', '名前・ニックネームを更新しました。'); document.getElementById('btn-close-modal').click(); document.activeElement.blur(); if (context.handleGetUserInfo) context.handleGetUserInfo(); }
      } else if (modalMode === 'email') {
        const res = await apiFetch('/auth/me/email', { method: 'PUT', body: JSON.stringify({ email: editForm.email }) });
        if (res.ok) { setProfile({ ...profile, email: editForm.email }); showNotification('success', 'メールアドレスを変更しました。'); document.getElementById('btn-close-modal').click(); document.activeElement.blur(); if (context.handleGetUserInfo) context.handleGetUserInfo(); }
      } else if (modalMode === 'password') {
        if (editForm.new_password !== editForm.confirm_password) { showNotification('error', '新しいパスワード（確認用）が一致しません。'); setLoading(false); return; }
        const res = await apiFetch('/auth/change-password', { method: 'POST', body: JSON.stringify({ current_password: editForm.current_password, new_password: editForm.new_password }) });
        if (res.ok) { showNotification('success', 'パスワードを正常に変更しました。'); document.getElementById('btn-close-modal').click(); document.activeElement.blur(); }
      } else if (modalMode === 'company') {
        const res = await apiFetch(`/companies/${company.uuid}`, {
          method: 'PUT',
          body: JSON.stringify({
            name_of_company: editForm.name_of_company, email: editForm.company_email, name_of_representative: editForm.name_of_representative,
            zipcode: editForm.zipcode, address1: editForm.address1, address2: editForm.address2, address3: editForm.address3, address4: editForm.address4,
            phone: editForm.phone, phone2: editForm.phone2, fax: editForm.fax, memo: editForm.memo
          })
        });
        if (res.ok) {
          setCompany({ ...company, name_of_company: editForm.name_of_company, email: editForm.company_email, name_of_representative: editForm.name_of_representative, zipcode: editForm.zipcode, address1: editForm.address1, address2: editForm.address2, address3: editForm.address3, address4: editForm.address4, phone: editForm.phone, phone2: editForm.phone2, fax: editForm.fax, memo: editForm.memo });
          showNotification('success', '会社情報をすべて更新しました。');
          document.getElementById('btn-close-modal').click(); document.activeElement.blur();
          if (context.handleGetUserInfo) context.handleGetUserInfo();
        } else { showNotification('error', '会社情報の更新に失敗しました。'); }
      }
    } catch (error) { showNotification('error', '処理に失敗しました。'); } finally { setLoading(false); }
  };

  const handleRegisterInstagram = async (e) => {
    e.preventDefault();
    if (!instagramName.trim()) return;
    setLoading(true);
    try {
      const response = await apiFetch(`/instagrams/sync?username=${encodeURIComponent(instagramName)}&is_new=true`, { method: 'POST' }, true);
      if (response.ok) {
        showNotification('success', `@${instagramName} を登録・同期しました！`);
        setInstagramName('');
        fetchInitialData();
      } else {
        const errorData = await response.json();
        showNotification('error', errorData.detail || '登録に失敗しました。');
      }
    } catch (error) {
      showNotification('error', '通信エラーが発生しました。');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncNow = async (username) => {
    setSyncingUser(username);
    try {
      const response = await apiFetch(`/instagrams/sync?username=${encodeURIComponent(username)}`, { method: 'POST' });
      if (response.ok) {
        showNotification('success', `@${username} を同期更新しました！`);
        fetchInitialData();
      }
    } catch (error) {
      showNotification('error', '通信エラー');
    } finally {
      setSyncingUser(null);
    }
  };

  // 🌟 新機能：公開設定切り替え用関数
  const handleToggleStatus = async (username, currentStatus) => {
    const newStatus = !currentStatus;
    try {
      const response = await apiFetch(`/instagrams/accounts/${encodeURIComponent(username)}/status?is_public=${newStatus}`, {
        method: 'PUT'
      });
      if (response.ok) {
        showNotification('success', `@${username} の公開設定を変更しました。`);
        fetchInitialData();
      } else {
        showNotification('error', 'ステータスの変更に失敗しました。');
      }
    } catch (error) {
      console.error("通信エラー", error);
      showNotification('error', '通信エラーが発生しました。');
    }
  };

  return (
    <div>
      <ol className="breadcrumb float-xl-end">
        <li className="breadcrumb-item"><a href="#/">Home</a></li>
        <li className="breadcrumb-item"><a href="#/">Settings</a></li>
        <li className="breadcrumb-item active">General Settings</li>
      </ol>
      <h1 className="page-header">Settings <small>各種設定と外部連携の管理</small></h1>
      <hr className="mb-4" />

      {notification.text && (
        <div className={`alert ${notification.type === 'success' ? 'alert-success' : notification.type === 'error' ? 'alert-danger' : 'alert-info'} alert-dismissible fade show mb-4`} role="alert">
          {notification.text}
        </div>
      )}

      <div className="row">
        <div style={{ width: '230px' }}>
          <nav className="navbar navbar-sticky d-none d-xl-block my-n4 py-4 h-100 text-end">
            <nav className="nav" id="bsSpyTarget">
              <a className="nav-link active" href="#general" data-toggle="scroll-to">General (一般設定)</a>
              <a className="nav-link" href="#security" data-toggle="scroll-to">Security (セキュリティ)</a>
              <a className="nav-link" href="#company" data-toggle="scroll-to">Company (会社詳細)</a>
              <a className="nav-link" href="#instagram" data-toggle="scroll-to">Instagram 連携</a>
            </nav>
          </nav>
        </div>

        <div className="col-xl-8" id="bsSpyContent">
          <ProfileSection profile={profile} onOpenModal={openEditModal} />
          <PasswordSection onOpenModal={openEditModal} />
          <CompanySection company={company} canEdit={canEditCompany} onOpenModal={openEditModal} />
          {/* 🌟 onToggleStatus 属性を追加 */}
          <InstagramSection
            instagramName={instagramName}
            setInstagramName={setInstagramName}
            registeredUsers={registeredUsers}
            onRegister={handleRegisterInstagram}
            onSyncNow={handleSyncNow}
            onToggleStatus={handleToggleStatus}
            syncingUser={syncingUser}
            loading={loading}
          />
        </div>
      </div>

      {/* 共通モーダル本体 */}
      <div className="modal fade" id="modalEdit" tabIndex="-1" aria-hidden="true">
        <div className={`modal-dialog ${modalMode === 'company' ? 'modal-lg' : ''}`}>
          <form onSubmit={handleModalSave} className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {modalMode === 'image' && 'プロフィール画像の変更'}
                {modalMode === 'name' && '名前・ニックネームの編集'}
                {modalMode === 'email' && 'メールアドレスの変更'}
                {modalMode === 'password' && 'パスワードのセキュリティ更新'}
                {modalMode === 'company' && '会社詳細プロフィールの編集'}
              </h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" id="btn-close-modal" onClick={(e) => e.target.blur()}></button>
            </div>

            <div className="modal-body">
              {modalMode === 'image' && (
                <div className="text-center px-3 pb-2">
                  {!selectedImage ? (
                    <div
                      {...getRootProps()}
                      className={`p-5 rounded border ${isDragActive ? 'bg-light border-primary' : 'border-secondary'}`}
                      style={{ borderStyle: 'dashed', cursor: 'pointer', borderWidth: '2px', transition: 'all 0.2s' }}
                    >
                      <input {...getInputProps()} />
                      <i className={`fa fa-cloud-upload-alt fa-3x mb-3 ${isDragActive ? 'text-primary' : 'text-body text-opacity-50'}`}></i>
                      <p className="mb-0 fw-bold fs-16px">
                        {isDragActive ? 'ここに画像をドロップ！' : 'クリックまたは画像をドラッグ＆ドロップ'}
                      </p>
                      <p className="text-muted small mt-2 mb-0">PNG, JPG, GIFが使用可能です</p>
                    </div>
                  ) : (
                    <div className="p-4 border rounded bg-light" style={{ borderStyle: 'dashed', borderWidth: '2px', borderColor: '#b6c2d9' }}>
                      <img
                        src={previewUrl}
                        alt="Preview"
                        className="rounded-circle shadow-sm mb-3"
                        style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                      />
                      <div className="mb-3 fw-bold text-primary">{selectedImage.name}</div>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setSelectedImage(null); setPreviewUrl(null); }}>
                        選び直す
                      </button>
                    </div>
                  )}
                </div>
              )}

              {modalMode === 'name' && (
                <div>
                  <div className="mb-3"><label className="form-label fw-bold">お名前</label><div className="row"><div className="col-6"><span className="text-muted d-block mb-1" style={{ fontSize: '12px' }}>姓</span><input className="form-control" value={editForm.last_name || ''} onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })} required /></div><div className="col-6"><span className="text-muted d-block mb-1" style={{ fontSize: '12px' }}>名</span><input className="form-control" value={editForm.first_name || ''} onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })} required /></div></div></div>
                  <div className="mb-3"><label className="form-label fw-bold">ニックネーム</label><div className="input-group"><span className="input-group-text bg-light">@</span><input className="form-control" value={editForm.nickname || ''} onChange={(e) => setEditForm({ ...editForm, nickname: e.target.value })} /></div></div>
                </div>
              )}

              {modalMode === 'email' && (
                <div className="mb-3"><label className="form-label fw-bold">新しいメールアドレス</label><input type="email" className="form-control" value={editForm.email || ''} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} required /></div>
              )}

              {modalMode === 'password' && (
                <div>
                  <div className="mb-3"><label className="form-label fw-bold">現在のパスワード</label><input type="password" className="form-control" value={editForm.current_password || ''} onChange={(e) => setEditForm({ ...editForm, current_password: e.target.value })} required /></div>
                  <div className="mb-3"><label className="form-label fw-bold">新しいパスワード</label><input type="password" className="form-control" value={editForm.new_password || ''} onChange={(e) => setEditForm({ ...editForm, new_password: e.target.value })} required /></div>
                  <div className="mb-3"><label className="form-label fw-bold">新しいパスワード (確認用)</label><input type="password" className="form-control" value={editForm.confirm_password || ''} onChange={(e) => setEditForm({ ...editForm, confirm_password: e.target.value })} required /></div>
                </div>
              )}

              {modalMode === 'company' && (
                <div className="row">
                  <div className="col-md-6 mb-3"><label className="form-label fw-bold">会社名</label><input className="form-control" value={editForm.name_of_company || ''} onChange={(e) => setEditForm({ ...editForm, name_of_company: e.target.value })} required /></div>
                  <div className="col-md-6 mb-3"><label className="form-label fw-bold">代表者名</label><input className="form-control" value={editForm.name_of_representative || ''} onChange={(e) => setEditForm({ ...editForm, name_of_representative: e.target.value })} required /></div>
                  <div className="col-md-6 mb-3"><label className="form-label fw-bold">会社代表メール</label><input type="email" className="form-control" value={editForm.company_email || ''} onChange={(e) => setEditForm({ ...editForm, company_email: e.target.value })} /></div>
                  <div className="col-md-6 mb-3"><label className="form-label fw-bold">郵便番号</label><input className="form-control" value={editForm.zipcode || ''} onChange={(e) => setEditForm({ ...editForm, zipcode: e.target.value })} /></div>
                  <div className="col-md-6 mb-3"><label className="form-label fw-bold">住所1</label><input className="form-control" value={editForm.address1 || ''} onChange={(e) => setEditForm({ ...editForm, address1: e.target.value })} /></div>
                  <div className="col-md-6 mb-3"><label className="form-label fw-bold">住所2</label><input className="form-control" value={editForm.address2 || ''} onChange={(e) => setEditForm({ ...editForm, address2: e.target.value })} /></div>
                  <div className="col-md-6 mb-3"><label className="form-label fw-bold">住所3</label><input className="form-control" value={editForm.address3 || ''} onChange={(e) => setEditForm({ ...editForm, address3: e.target.value })} /></div>
                  <div className="col-md-6 mb-3"><label className="form-label fw-bold">住所4</label><input className="form-control" value={editForm.address4 || ''} onChange={(e) => setEditForm({ ...editForm, address4: e.target.value })} /></div>
                  <div className="col-md-4 mb-3"><label className="form-label fw-bold">電話番号1</label><input className="form-control" value={editForm.phone || ''} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} /></div>
                  <div className="col-md-4 mb-3"><label className="form-label fw-bold">電話番号2</label><input className="form-control" value={editForm.phone2 || ''} onChange={(e) => setEditForm({ ...editForm, phone2: e.target.value })} /></div>
                  <div className="col-md-4 mb-3"><label className="form-label fw-bold">FAX</label><input className="form-control" value={editForm.fax || ''} onChange={(e) => setEditForm({ ...editForm, fax: e.target.value })} /></div>
                  <div className="col-12 mb-0"><label className="form-label fw-bold">備考・メモ</label><textarea className="form-control" rows="3" value={editForm.memo || ''} onChange={(e) => setEditForm({ ...editForm, memo: e.target.value })}></textarea></div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              {modalMode === 'image' && profile.profile_image && (
                <button type="button" className="btn btn-danger me-auto" disabled={loading} onClick={handleDeleteImage}>
                  Delete
                </button>
              )}
              <button type="button" className="btn btn-default" data-bs-dismiss="modal" disabled={loading} onClick={(e) => e.target.blur()}>Close</button>
              <button type="submit" className="btn btn-theme" disabled={loading || (modalMode === 'image' && !selectedImage)}>
                {loading ? '保存中...' : 'Save changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;